/**
 * The "Two-step verification" card on /security (2026-09-26).
 *
 * Status in words (On since … / Off), the sign-in methods this person has set up here, what this
 * session has proven, and the three actions — each behind a step-up, each worded and tooltipped:
 *   - Turn on            only when the server says `canEnable` (two DIFFERENT methods set up)
 *   - Turn off           confirm → step-up → disable (the server returns a fresh token; we adopt it)
 *   - New recovery codes confirm → step-up → regenerate (old codes stop working)
 * Recovery codes are shown ONCE in a dialog with Copy / Download .txt / "I've saved them".
 * Nothing is reported as done until the server confirms it (hardening rule 16).
 */
import React from "react";
import {
    Box, Button, Code, Heading, HStack, Icon, List, ListItem, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader,
    ModalOverlay, SimpleGrid, Stack, Text, useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery } from "@apollo/client";
import { FiCheckCircle, FiCircle, FiCopy, FiDownload, FiLock, FiRefreshCw } from "react-icons/fi";
import { useAuth } from "../../../contexts/AuthContext";
import {
    DISABLE_TWO_FACTOR, ENABLE_TWO_FACTOR, MY_TWO_FACTOR_STATUS, REGENERATE_RECOVERY_CODES,
} from "../queries";
import { errorWords, recoveryCodesFileText, twoFactorView } from "../utils/assurance";
import type { TwoFactorStatus, TwoFactorEnableResult, RecoveryCodesResult, StepUpResult } from "../../../generated/graphql";
import { useStepUp } from "../hooks/useStepUp";
import {
    AuthError, AuthLoading, AuthStatusPill, AuthSurface, AuthTip, alpha, authWords, blurClick, useAuthConfirm, useAuthTheme,
} from "./ui";

/** The factor types a person can set up on a site, in display order (recovery codes are listed separately). */
const LISTED_FACTORS = ["passkey", "pwd", "email_code", "sms_code"] as const;

export interface TwoStepVerificationCardProps {
    /** Which methods this site allows (tenantAuthConfig) — a method the site switched off is not listed. */
    enabledFactors: { passkey: boolean; pwd: boolean; email_code: boolean; sms_code: boolean };
}

// ─── Recovery codes, shown once ──────────────────────────────────────────────────────────
const RecoveryCodesDialog: React.FC<{ codes: string[] | null; onDone: () => void }> = ({ codes, onDone }) => {
    const t = useAuthTheme();
    const toast = useToast();
    const site = typeof window !== "undefined" ? window.location.host : "this site";

    const copy = async () => {
        if (!codes) return;
        try {
            await navigator.clipboard.writeText(codes.join("\n"));
            toast({ title: "Recovery codes copied", description: "Paste them somewhere safe, like your password manager.", status: "success", duration: 3500 });
        } catch {
            toast({ title: "Couldn't copy", description: "Your browser blocked the clipboard. Use Download instead, or write them down.", status: "error", duration: 6000 });
        }
    };
    const download = () => {
        if (!codes) return;
        try {
            const blob = new Blob([recoveryCodesFileText(codes, site)], { type: "text/plain" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `recovery-codes-${site.replace(/[^a-z0-9.-]/gi, "-")}.txt`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch {
            toast({ title: "Couldn't download", description: "Use Copy instead, or write them down.", status: "error", duration: 6000 });
        }
    };

    return (
        <Modal isOpen={!!codes} onClose={() => { /* only "I've saved them" closes it */ }} closeOnOverlayClick={false} closeOnEsc={false} size={{ base: "full", sm: "lg" }} isCentered>
            <ModalOverlay />
            <ModalContent mx={{ base: 0, sm: 3 }} maxH={{ sm: "calc(100dvh - 48px)" }} overflowY="auto" data-testid="recovery-codes-dialog">
                <ModalHeader>
                    <Heading as="h2" size="md" color={t.text}>Save your recovery codes</Heading>
                    <Text mt={1} fontSize="md" fontWeight="normal" color={t.muted}>
                        This is the only time they are shown. If you can't use your other sign-in methods, one of these codes
                        confirms it's you. Each code works once.
                    </Text>
                </ModalHeader>
                <ModalBody>
                    <SimpleGrid columns={2} spacing={2} p={3} borderRadius="lg" bg={t.raised} data-testid="recovery-codes">
                        {(codes || []).map((c) => (
                            <Code key={c} fontSize="md" py={1} px={2} bg="transparent" color={t.text} textAlign="center" overflowWrap="anywhere">{c}</Code>
                        ))}
                    </SimpleGrid>
                    <Text mt={3} fontSize="sm" color={t.faint}>Keep them somewhere safe and private — a password manager, or printed in a drawer.</Text>
                </ModalBody>
                <ModalFooter>
                    <Stack direction={{ base: "column", sm: "row" }} spacing={3} w="full" justify="flex-end">
                        <AuthTip label="Copy all the codes to your clipboard. Nothing is sent anywhere.">
                            <Button size="lg" minH="48px" w={{ base: "full", sm: "auto" }} variant="outline" leftIcon={<FiCopy />} onClick={blurClick(() => void copy())}>Copy</Button>
                        </AuthTip>
                        <AuthTip label="Save the codes as a .txt file on this device. Nothing is sent anywhere.">
                            <Button size="lg" minH="48px" w={{ base: "full", sm: "auto" }} variant="outline" leftIcon={<FiDownload />} onClick={blurClick(download)}>Download .txt</Button>
                        </AuthTip>
                        <AuthTip label="Close this. The codes will not be shown again — make sure you've saved them.">
                            <Button size="lg" minH="48px" w={{ base: "full", sm: "auto" }} colorScheme="blue" onClick={onDone}>I've saved them</Button>
                        </AuthTip>
                    </Stack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export const TwoStepVerificationCard: React.FC<TwoStepVerificationCardProps> = ({ enabledFactors }) => {
    const t = useAuthTheme();
    const toast = useToast();
    const auth = useAuth();
    const { requireStepUp, withStepUp, stepUpDialog } = useStepUp();
    const { confirm, confirmDialog } = useAuthConfirm();
    const { data, loading, error, refetch } = useQuery<{ myTwoFactorStatus: TwoFactorStatus }>(MY_TWO_FACTOR_STATUS, { fetchPolicy: "network-only" });
    const [enableMut] = useMutation<{ enableTwoFactor: TwoFactorEnableResult }>(ENABLE_TWO_FACTOR);
    const [disableMut] = useMutation<{ disableTwoFactor: StepUpResult }>(DISABLE_TWO_FACTOR);
    const [regenerateMut] = useMutation<{ regenerateRecoveryCodes: RecoveryCodesResult }>(REGENERATE_RECOVERY_CODES);
    const [busy, setBusy] = React.useState<null | "on" | "off" | "codes">(null);
    const [codes, setCodes] = React.useState<string[] | null>(null);

    const adopt = async (token?: string | null) => {
        if (!token) return;
        if (auth.adoptToken) await auth.adoptToken(token);
        else { localStorage.setItem("auth_token", token); await auth.refreshAuth?.(); }
    };

    const turnOn = async () => {
        setBusy("on");
        try {
            if (!(await requireStepUp({ reason: "Before turning on two-step verification, confirm it's you with a second sign-in method." }))) return;
            const res = await withStepUp(() => enableMut());
            const out = res.data?.enableTwoFactor;
            if (!out?.token) throw new Error("The server didn't confirm it. Two-step verification is still off.");
            await adopt(out.token);
            setCodes(out.recoveryCodes || []);
            toast({ title: "Two-step verification is on", status: "success", duration: 3500 });
            await refetch();
        } catch (err) {
            toast({ title: "Couldn't turn it on", description: errorWords(err), status: "error", duration: 7000 });
        } finally { setBusy(null); }
    };

    const turnOff = async () => {
        const ok = await confirm({
            title: "Turn off two-step verification?",
            body: "Sensitive pages will stop asking for a second sign-in method, and your recovery codes stop working. You can turn it on again at any time.",
            confirmLabel: "Turn it off",
            tone: "danger",
        });
        if (!ok) return;
        setBusy("off");
        try {
            if (!(await requireStepUp({ reason: "Before turning off two-step verification, confirm it's you with a second sign-in method." }))) return;
            const res = await withStepUp(() => disableMut());
            const out = res.data?.disableTwoFactor;
            if (!out?.token) throw new Error("The server didn't confirm it. Two-step verification is still on.");
            await adopt(out.token);
            toast({ title: "Two-step verification is off", status: "success", duration: 3500 });
            await refetch();
        } catch (err) {
            toast({ title: "Couldn't turn it off", description: errorWords(err), status: "error", duration: 7000 });
        } finally { setBusy(null); }
    };

    const newCodes = async () => {
        const ok = await confirm({
            title: "Make new recovery codes?",
            body: "You get 10 new codes, shown once. Your old recovery codes stop working straight away.",
            confirmLabel: "Make new codes",
            tone: "warning",
        });
        if (!ok) return;
        setBusy("codes");
        try {
            if (!(await requireStepUp({ reason: "Before making new recovery codes, confirm it's you with a second sign-in method." }))) return;
            const res = await withStepUp(() => regenerateMut());
            const out = res.data?.regenerateRecoveryCodes;
            if (!out?.token) throw new Error("The server didn't confirm it. Your old codes still work.");
            await adopt(out.token);
            setCodes(out.recoveryCodes || []);
            await refetch();
        } catch (err) {
            toast({ title: "Couldn't make new codes", description: errorWords(err), status: "error", duration: 7000 });
        } finally { setBusy(null); }
    };

    const header = (pill?: React.ReactNode) => (
        <HStack justify="space-between" align="start" mb={3} flexWrap="wrap" gap={2}>
            <HStack spacing={3}>
                <Box as={FiLock} color={t.text} aria-hidden />
                <Heading size="sm" color={t.text}>Two-step verification</Heading>
            </HStack>
            {pill}
        </HStack>
    );

    let content: React.ReactNode;
    if (loading && !data) {
        content = <>{header()}<AuthLoading label="Checking two-step verification" cards={1} columns={{ base: 1 }} /></>;
    } else if (error || !data?.myTwoFactorStatus) {
        content = <>{header()}<AuthError title="Couldn't load two-step verification" error={error || "No answer from the server."} onRetry={() => refetch()} /></>;
    } else {
        const status = data.myTwoFactorStatus;
        const view = twoFactorView(status);
        const has = new Set(status.factorTypes || []);
        const listed = LISTED_FACTORS.filter((f) => enabledFactors[f] || has.has(f));
        const sessionAmr = (status.sessionAmr || []).filter((f) => f !== "legacy_code");
        const pillStatus = view.state === "on" ? "ON" : view.state === "ready" ? "READY" : "NOT_READY";

        content = (
            <>
                {header(<AuthStatusPill kind="twoFactor" status={pillStatus} />)}
                <Stack spacing={4}>
                    <Text fontSize="md" color={t.muted}>
                        When it's on, pages and actions that hold sensitive things ask you to confirm it's you with a
                        second, different sign-in method. Signing in itself doesn't change.
                    </Text>
                    <Text fontSize="lg" fontWeight="semibold" color={t.text} data-testid="two-step-headline">{view.headline}</Text>

                    <Box>
                        <Text fontSize="md" fontWeight="semibold" color={t.text} mb={2}>Your sign-in methods on this site</Text>
                        <List spacing={2} data-testid="two-step-factors">
                            {listed.map((f) => {
                                const set = has.has(f);
                                return (
                                    <ListItem key={f} display="flex" alignItems="center" gap={2}>
                                        <Icon as={set ? FiCheckCircle : FiCircle} color={set ? t.success : t.faint} aria-hidden />
                                        <Text fontSize="md" color={t.text}>{authWords("factor", f)}</Text>
                                        <Text fontSize="md" color={set ? t.muted : t.faint}>— {set ? "set up" : "not set up"}</Text>
                                    </ListItem>
                                );
                            })}
                        </List>
                    </Box>

                    {view.state === "not-ready" && (
                        <Box p={3} borderRadius="lg" bg={alpha(t.warning, 0.1)} borderWidth="1px" borderColor={alpha(t.warning, 0.3)} data-testid="two-step-not-ready">
                            <Text fontSize="md" color={t.text}>{view.why}</Text>
                        </Box>
                    )}

                    <Text fontSize="md" color={t.muted} data-testid="two-step-session">
                        {status.sessionAal === 2
                            ? `This session: confirmed with two methods${sessionAmr.length ? ` (${sessionAmr.map((f) => authWords("factor", f)).join(" + ")})` : ""}.`
                            : `This session: signed in with one method${sessionAmr.length ? ` (${sessionAmr.map((f) => authWords("factor", f)).join(", ")})` : ""}.`}
                    </Text>

                    {view.state === "on" && (
                        <Text fontSize="md" color={view.codesLow ? t.text : t.muted} fontWeight={view.codesLow ? "semibold" : "normal"} data-testid="two-step-codes-left">
                            {view.codesLine}{view.codesLow ? " — make new ones soon so you're never locked out." : "."}
                        </Text>
                    )}

                    {(view.showTurnOn || view.showTurnOff || view.showNewCodes) && (
                        <Stack direction={{ base: "column", sm: "row" }} spacing={3}>
                            {view.showTurnOn && (
                                <AuthTip label={"Turn on two-step verification.\n\nWhat happens when you click:\n  1. You confirm it's you with a second sign-in method\n  2. Two-step verification turns on for your account\n  3. You get 10 recovery codes, shown once — save them"}>
                                    <Button size="lg" minH="48px" w={{ base: "full", sm: "auto" }} colorScheme="blue" isLoading={busy === "on"} loadingText="Turning on…" isDisabled={!!busy} onClick={() => void turnOn()}>
                                        Turn on
                                    </Button>
                                </AuthTip>
                            )}
                            {view.showNewCodes && (
                                <AuthTip label={"Make 10 new recovery codes.\n\nWhat happens when you click:\n  1. You're asked to confirm\n  2. You confirm it's you with a second sign-in method\n  3. New codes are shown once; your old codes stop working"}>
                                    <Button size="lg" minH="48px" w={{ base: "full", sm: "auto" }} variant="outline" leftIcon={<FiRefreshCw />} isLoading={busy === "codes"} loadingText="Making codes…" isDisabled={!!busy} onClick={() => void newCodes()}>
                                        New recovery codes
                                    </Button>
                                </AuthTip>
                            )}
                            {view.showTurnOff && (
                                <AuthTip label={"Turn off two-step verification.\n\nWhat happens when you click:\n  1. You're asked to confirm\n  2. You confirm it's you with a second sign-in method\n  3. It turns off and your recovery codes stop working"}>
                                    <Button size="lg" minH="48px" w={{ base: "full", sm: "auto" }} variant="outline" colorScheme="red" isLoading={busy === "off"} loadingText="Turning off…" isDisabled={!!busy} onClick={() => void turnOff()}>
                                        Turn off
                                    </Button>
                                </AuthTip>
                            )}
                        </Stack>
                    )}
                </Stack>
            </>
        );
    }

    return (
        <AuthSurface data-testid="two-step-card">
            {content}
            {stepUpDialog}
            {confirmDialog}
            <RecoveryCodesDialog codes={codes} onDone={() => setCodes(null)} />
        </AuthSurface>
    );
};

export default TwoStepVerificationCard;
